import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Tag,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import {
  EllipsisOutlined,
  EditFilled,
  DeleteOutlined,
  UserAddOutlined,
} from "@ant-design/icons";

import CommonTable from "../../common/CommonTable";
import deactivateIcon from "../../assets/svg/user-times-solid.svg";
import EditUser from "./EditUser";
import DeactivateUser from "./DeactivateUser";
import { useUserManagement } from "./hooks/useUserManagement";
import { getUserInfo } from "../../utils/utils";
import { searchTeams } from "../../actionCreator/teamManagementActionCreator";
import { searchRoles } from "../../actionCreator/roleManagementActionCreator";
const { Search } = Input;
const { Option } = Select;

const statusOptions = [
  { value: "all", label: "All Users" },
  { value: "true", label: "Active Users" },
  { value: "false", label: "Inactive Users" },
];

const actionsOptions = [
  { value: "all", label: "Select Action" },
  { value: "deactivate", label: "Deactivate" },
  { value: "delete", label: "Delete" },
];

const UserListing = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const {
    loading: isLoading,
    handleDelete,
    onFilterChange,
    handleActivateDeactivateUser,
    handleBulkAction,
  } = useUserManagement();
  const { id: userId } = getUserInfo();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const [action, setAction] = useState();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [user, setUser] = useState();
  const [teams, setTeams] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleFetching, setRoleFetching] = useState(false);
  const users = useSelector((state) => state.UserManagementReducer.users);
  const activeUserCount = useSelector(
    (state) => state.UserManagementReducer.activeUserCount
  );
  const loading = useSelector((state) => state.UserManagementReducer.loading);

  const columns = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "firstName",
      sorter: true,

      render: (record, row) => {
        return (
          <>
            {row?.firstName} {row?.lastName}
          </>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      sorter: true,
    },
    {
      title: "Team/Hotel",
      dataIndex: "team",
      key: "team",
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (record, row) => {
        return (
          <>
            {record ? (
              <Tag bordered={false} className="active-tag">
                Active
              </Tag>
            ) : (
              <Tag bordered={false} className="deactived-tag">
                Inactive
              </Tag>
            )}
          </>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      render: (record, row) => {
        const items = [
          {
            key: "1",
            label: (
              <Row
                gutter={row?.active ? 12 : 24}
                className="heading14px"
                style={{ color: "#29292B" }}
                onClick={() => {
                  setAction("edit");
                  setUser(row);
                }}
              >
                <Col span={6}>
                  <EditFilled />
                </Col>
                <Col span={18}>Edit</Col>
              </Row>
            ),
          },
          {
            key: "2",
            label: (
              <Row
                gutter={row?.active ? 12 : 24}
                className="heading14px"
                style={{ color: "#29292B" }}
                onClick={() => {
                  setAction("deactivate");
                  setUser(row);
                }}
              >
                <Col span={6}>
                  {row?.active ? (
                    <img
                      src={deactivateIcon}
                      alt=""
                      style={{ width: 18, height: 18 }}
                    />
                  ) : (
                    <UserAddOutlined />
                  )}
                </Col>

                <Col span={18}>{row?.active ? "Deactivate" : "Activate"}</Col>
              </Row>
            ),
          },
          {
            key: "3",
            label: (
              <Row
                gutter={row?.active ? 12 : 24}
                style={{ color: "#29292B" }}
                onClick={() => {
                  setAction("delete");
                  setUser(row);
                }}
              >
                <Col span={6}>
                  <DeleteOutlined className="heading14px text-error" />
                </Col>

                <Col span={18} className="heading14px text-error">
                  Delete
                </Col>
              </Row>
            ),
          },
        ].filter(Boolean);

        return (
          <>
            {userId === row?.id ? (
              <></>
            ) : (
              <Dropdown
                menu={{
                  items,
                }}
                trigger={["click"]}
              >
                <Button type="text" icon={<EllipsisOutlined />} />
              </Dropdown>
            )}
          </>
        );
      },
    },
  ];

  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      if (selected) {
        setSelectedUsers([...selectedUsers, record]);
      } else {
        setSelectedUsers(selectedUsers?.filter((d) => d?.id !== record?.id));
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      setSelectedUsers(selectedRows);
    },
    selectedRowKeys: selectedUsers?.map((d) => d?.id),
  };

  const onSearch = debounce((event) => {
    onFilterChange(
      "globalSearchTerm",
      event?.target?.value ? event?.target?.value : undefined
    );
    onFilterChange("page", 1);
  }, 500);

  useEffect(() => {
    const fetchDefault = async () => {
      try {
        const res = await dispatch(searchTeams());
        setTeams(res?.content || []);
        const response = await dispatch(searchRoles());
        setRoles(response?.roles || []);
      } catch (error) {}
    };

    fetchDefault();
  }, [dispatch]);

  const fetchOptions = debounce(async (value) => {
    setFetching(true);
    try {
      const res = await dispatch(searchTeams(value));
      setTeams(res?.content || []);
    } catch (err) {}
    setFetching(false);
  }, 500);

  const fetchRoleOptions = debounce(async (value) => {
    setRoleFetching(true);
    try {
      const res = await dispatch(searchRoles(value));
      setRoles(res?.roles || []);
    } catch (err) {}
    setRoleFetching(false);
  }, 500);

  return (
    <>
      <Card
        title={
          <div className="d-flex align-items-center justify-content-between text-theme heading18px users-listing-filters">
            <span className="d-flex align-items-center gap-10">
              Users{" "}
              <span className="heading12px text-color-6E6E6E">
                ({activeUserCount} active)
              </span>
            </span>
            <div className="d-flex align-items-center gap-10 filters">
              <Search
                defaultValue={searchParams.get("globalSearchTerm")}
                allowClear
                onChange={onSearch}
              />
              <Select
                value={searchParams.get("isActive") || "all"}
                optionLabelProp="label"
                options={statusOptions}
                style={{ width: 130 }}
                onChange={(value) => {
                  onFilterChange("isActive", value);
                  onFilterChange("page", 1);
                }}
              />
              <Select
                value={searchParams.get("team") || "All Teams"}
                showSearch
                placeholder="Search team..."
                onSearch={fetchOptions}
                filterOption={false}
                notFoundContent={fetching ? <Spin size="small" /> : null}
                allowClear
                style={{ width: 130 }}
                onChange={(value) => {
                  onFilterChange("team", value);
                  onFilterChange("page", 1);
                }}
              >
                {teams.map((item) => (
                  <Option key={item.id} value={item.teamName}>
                    {item.teamName}
                  </Option>
                ))}
              </Select>

              <Select
                value={searchParams.get("role") || "All Roles"}
                showSearch
                placeholder="Search role..."
                onSearch={fetchRoleOptions}
                filterOption={false}
                notFoundContent={roleFetching ? <Spin size="small" /> : null}
                allowClear
                style={{ width: 130 }}
                onChange={(value) => {
                  onFilterChange("role", value);
                  onFilterChange("page", 1);
                }}
              >
                {roles.map((item) => (
                  <Option key={item.id} value={item.name}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        }
        className="rounded-2xl shadow-md"
      >
        {!!selectedUsers?.length && (
          <div className="d-flex align-items-center justify-content-end gap-10">
            <Form
              form={form}
              layout="inline"
              className="mb20"
              onFinish={(value) => setAction(value?.action)}
            >
              <Form.Item
                name="action"
                initialValue="all"
                rules={[{ required: true, message: "Please select action" }]}
              >
                <Select
                  optionLabelProp="label"
                  options={actionsOptions}
                  style={{ width: 130 }}
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" className="theme-btn">
                  Apply
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
        <CommonTable
          rowSelection={{
            type: "checkbox",
            ...rowSelection,
          }}
          columns={columns}
          dataSource={users?.content}
          loading={loading}
          showSorterTooltip={true}
          sortDirections={["ascend", "descend"]}
          pagination={{
            pageSize: 10,
            total: users?.totalElements,
            defaultCurrent: 1,
            current: page || 1,
            onChange: (page, pageSize) => {
              onFilterChange("page", page);
            },
          }}
          onChange={(pagination, filters, sorter) => {
            onFilterChange(
              "sort",
              !!sorter?.field
                ? sorter?.order === "ascend"
                  ? sorter?.field + ",asc"
                  : sorter?.field + ",desc"
                : undefined
            );
          }}
        />
      </Card>
      <EditUser
        user={user}
        isOpen={action === "edit"}
        closeModal={() => {
          setAction();
          setUser();
        }}
      />
      <DeactivateUser
        user={user}
        isLoading={isLoading}
        isOpen={action === "deactivate" || action === "delete"}
        isDelete={action === "delete"}
        selectedUsers={selectedUsers}
        handleCancel={() => {
          setAction();
          setUser();
        }}
        handleAction={() => {
          if (action === "delete") {
            if (user) {
              handleDelete(user);
            } else {
              handleBulkAction(
                {
                  action: "DELETE",
                  userIds: selectedUsers
                    ?.filter((i) => i?.id !== userId)
                    ?.map((d) => d?.id),
                },
                () => form.resetFields()
              );
            }
          } else {
            if (user) {
              handleActivateDeactivateUser(user);
            } else {
              handleBulkAction(
                {
                  action: "DEACTIVATE",
                  userIds: selectedUsers
                    ?.filter((i) => i?.id !== userId)
                    ?.map((d) => d?.id),
                },
                () => form.resetFields()
              );
            }
          }
        }}
      />
    </>
  );
};

export default UserListing;
